/**
 * @file Слой 0 - Живое поле созерцания
 * @module membrane/src/layers/Layer0Field
 * 
 * Первый слой мембраны - живая среда, существующая ДО любого клика.
 * Визуальная метафора: космическая туманность в облачных цветах.
 * 
 * Использует модульный WebGL рендеринг через Field0Canvas.
 */

import { BaseLayerProps } from '../types/membrane';
import { useState } from 'react';
import { motion } from 'framer-motion';
import Field0Canvas from '../visual/field0/Field0Canvas';

/**
 * Компонент Layer0Field - живое поле созерцания
 * 
 * Космическая туманность в облачных цветах:
 * - Наполненная энергия, постоянно в процессе
 * - Облака формируются как в небе - постепенно появляются, растут, меняют форму
 * - Облака переливаются в зависимости от положения мыши
 * - Облака движутся как процесс (формируются, скапливаются, разрастаются, сужаются)
 * - При скролле мышки в локации - приближение/удаление к участку облаков
 * - Жизнь происходит до первого клика
 */
export default function Layer0Field({ onProceed, state: _state }: BaseLayerProps) {
  const [hasClicked, setHasClicked] = useState(false);

  // Обработка первого клика - углубление состояния, затем кристаллизация
  const handleFirstClick = (x: number, y: number) => {
    if (!hasClicked && onProceed) {
      setHasClicked(true);
      
      // Передаём координаты клика через кастомное событие
      const customEvent = new CustomEvent('membrane-click', {
        detail: { x, y },
      });
      window.dispatchEvent(customEvent);
      
      // Углубление состояния: туман светлеет, появляется ощущение глубины
      // Переход к кристаллизации происходит после углубления
      setTimeout(() => {
        onProceed();
      }, 1200); // Больше времени для ощущения углубления
    }
  };

  return (
    <div 
      className="absolute inset-0 w-full h-full overflow-hidden"
      style={{
        backgroundColor: 'transparent', // Убеждаемся, что фон прозрачный
      }}
    >
      {/* WebGL Canvas с живым полем туманности */}
      <Field0Canvas
        onFirstClick={handleFirstClick}
        className="absolute inset-0 w-full h-full"
      />
      
      {/* Индикатор первого клика (появляется через время, очень мягко) */}
      {!hasClicked && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.4 }}
          transition={{ delay: 3, duration: 2 }}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2 text-white text-sm font-extralight pointer-events-none"
          style={{
            textShadow: '0 0 10px rgba(255, 255, 255, 0.5), 0 0 20px rgba(240, 245, 255, 0.3)',
            letterSpacing: '1px',
          }}
        >
          {/* Убрали текст - образ присутствует, не объясняет */}
        </motion.div>
      )}
    </div>
  );
}
